<template>
    <div :class="classes">
        <slot/>
    </div>
</template>

<script setup lang="ts">
import { PropType, ref, onMounted, onBeforeUnmount } from 'vue';
import viewportEvents, { Viewport } from '@/core/responsive/viewport/viewportEvents.service';
import range from 'lodash-es/range';

type CallbackType = (on: boolean) => void;

interface ExtendedScrollPointDefinition {
    clazz?: string;
    callback?: CallbackType;
}

type ScrollPointDefinition = string | CallbackType | ExtendedScrollPointDefinition;
type ScrollDirection = 'up' | 'down';

export interface ScrollTriggerOptions {
    scrollingDown?: ScrollPointDefinition,
    scrollingUp?: ScrollPointDefinition,

    [key: number]: ScrollPointDefinition
}

const MinVelocity = 0.2;
let customScrollPointIds: string[];
const finalOptions = new Map<string, ExtendedScrollPointDefinition>();

const props = defineProps({
    options: {
        type: Object as PropType<ScrollTriggerOptions>,
        default: () => ({})
    }
});

const classes = ref<string[]>([]);
const oldScrollY = ref<number>(0);
const oldTimestamp = ref<number>(0);
const scrollDirection = ref<ScrollDirection | null>(null);

onMounted(() => {
    viewportEvents.setCallback(viewportEventHandler);

    const { scrollingDown, scrollingUp, ...customScrollDefinitions } = props.options;
    customScrollPointIds = Object.keys(customScrollDefinitions);

    // Create array with room for all classes
    classes.value = range(Object.keys(props.options).length).map(() => '');

    // fixup all scroll-definitions so we can handle them faster runtime
    Object.keys(props.options).forEach(spKey => {
        finalOptions.set(spKey, createExtendedDefinition(spKey, props.options[spKey]));
    });

    function createExtendedDefinition(key: string, scrollPoint: ScrollPointDefinition): ExtendedScrollPointDefinition {
        let clazz = `_${key}_`; // Fallback - in order to keep track of whether callback has been fired.
        let callback;
        if (typeof scrollPoint === 'string') {
            clazz = scrollPoint;
        } else if (typeof scrollPoint === 'function') {
            callback = scrollPoint;
        } else {
            clazz = scrollPoint.clazz || clazz;
            callback = scrollPoint.callback;
        }

        return {
            clazz,
            callback
        };
    }
});

onBeforeUnmount(() => {
    viewportEvents.removeCallback(viewportEventHandler);
});

function viewportEventHandler(viewport: Viewport, timestamp: number) {
    const scrollY = viewport.scrollY;
    if (scrollY === oldScrollY.value) return;
    handleScroll(scrollY, timestamp);
    oldScrollY.value = scrollY;
    oldTimestamp.value = timestamp;
}

function handleScroll(scrollY: number, timestamp: number) {
    handleUpDown(scrollY, timestamp);
    handleScrollPoints(scrollY);
}

function handleUpDown(scrollY: number, timestamp: number) {
    const currentScrollDirection: ScrollDirection = scrollY > oldScrollY.value ? 'down' : 'up';

    if (currentScrollDirection === scrollDirection.value) {
        // No changes
        return;
    }

    if (currentScrollDirection === 'down') {
        setScrollPoint('scrollingDown', 0);
        clearScrollPoint('scrollingUp', 1);
        scrollDirection.value = 'down';
    } else {
        // Change to scrolling up only if fast enough
        const velocity = Math.abs((scrollY - oldScrollY.value) / (timestamp - oldTimestamp.value));
        if (velocity >= MinVelocity) {
            setScrollPoint('scrollingUp', 1);
            clearScrollPoint('scrollingDown', 0);
            scrollDirection.value = 'up';
        }
    }
}

function handleScrollPoints(scrollY: number) {
    customScrollPointIds.forEach((scrollPosId, ix) => {
        if (scrollY >= parseInt(scrollPosId, 10)) {
            setScrollPoint(scrollPosId, 2 + ix);
        } else {
            clearScrollPoint(scrollPosId, 2 + ix);
        }
    });
}

function setScrollPoint(scrollPointId: string, position: number) {
    const scrollPointDef = finalOptions.get(scrollPointId);
    if (!scrollPointDef) return;

    // Already set ?
    if (classes.value[position]) return;

    classes.value[position] = scrollPointDef.clazz || '';
    const cb = scrollPointDef.callback;
    if (cb) {
        cb(true);
    }
}

function clearScrollPoint(scrollPointId: string, position: number) {
    const scrollPointDef = finalOptions.get(scrollPointId);
    if (!scrollPointDef) return;

    // Already cleared ?
    if (!classes.value[position]) return;

    classes.value[position] = '';

    const cb = scrollPointDef.callback;
    if (cb) {
        cb(false);
    }
}
</script>
